import { useState, useEffect, useCallback, useRef } from "react";
import type { RiskSnapshot, DataStatus } from "../types";

/**
 * Polls the R2-backed API for the latest risk snapshot every `intervalMs`.
 *
 * In development, reads from a local fixture file.
 * In production, reads from the Cloudflare Worker endpoint.
 */

const API_URL = import.meta.env.PROD
  ? "https://defi-risk-api.osepper.workers.dev/latest"  // production Worker
  : "/fixtures/snapshot-sample.json";           // local dev fixture

const POLL_INTERVAL_MS = 60_000; // 60 seconds
const STALE_THRESHOLD_MS = 1_800_000; // 30 min (2x pipeline interval of 15 min)

export function useRiskData(intervalMs: number = POLL_INTERVAL_MS) {
  const [data, setData] = useState<RiskSnapshot | null>(null);
  const [status, setStatus] = useState<DataStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(API_URL);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const snapshot: RiskSnapshot = await res.json();
      setData(snapshot);
      setLastFetched(new Date());
      setError(null);

      // Check data freshness (pipeline timestamp, not fetch time)
      const dataAge = Date.now() - new Date(snapshot.timestamp).getTime();
      setStatus(dataAge < STALE_THRESHOLD_MS ? "fresh" : "stale");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      setStatus("error");
      // Keep stale data visible rather than clearing it
    }
  }, []);

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, intervalMs);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchData, intervalMs]);

  return { data, status, error, lastFetched, refetch: fetchData };
}
